import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaUserPlus,
  FaSearch,
  FaFilter,
  FaSpinner,
  FaSyncAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import {
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";

import { API_BASE } from "../../../ApiUrl";
import "./Users.css";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState({
    email: "",
    mobile_country_code: "+91",
    mobile_number: "",
    password: "",
    role: "",
    is_active: true,
    mfa_method: "EMAIL",
  });

  const MFA_OPTIONS = ["EMAIL", "AUTHENTICATOR"];

  // ---------------------------------------------------------------------
  // Auth helpers
  // ---------------------------------------------------------------------

  const getSuperAdminUser = () => {
    try {
      const raw = localStorage.getItem("super_admin_user");
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.error(
        "Failed to parse super_admin_user from localStorage:",
        err
      );
      return null;
    }
  };

  const getRoleNames = (user) => {
    if (!Array.isArray(user.role) || user.role.length === 0) {
      return "-";
    }

    const names = user.role
      .map((id) => roles.find((r) => r.id === id)?.name)
      .filter(Boolean);

    return names.length > 0 ? names.join(", ") : "-";
  };

  // ---------------------------------------------------------------------
  // Fetching
  // ---------------------------------------------------------------------

  const fetchUsers = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/users/`);

      console.log("Users API Response:", response.data);

      let userData = [];

      if (response.data.success && response.data.data) {
        userData = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
      } else if (Array.isArray(response.data)) {
        userData = response.data;
      } else {
        userData = [response.data];
      }

      setUsers(userData);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching users:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch users. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_BASE}/roles/`);

      console.log("Roles API Response:", response.data);

      if (response.data.success && Array.isArray(response.data.data)) {
        const roleData = response.data.data
          .filter(
            (item) =>
              item.roles_for &&
              item.roles_for.toLowerCase() === "super admin"
          )
          .map((item) => ({
            id: item.id,
            name: item.name || item.code || `Role ${item.id}`,
          }));

        setRoles(roleData);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch roles. Please try again.",
      });

      setRoles([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // ---------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------

  useEffect(() => {
    if (roles.length === 0) {
      setFilteredUsers([]);
      setCurrentPage(1);
      return;
    }

    const superAdminRoleIds = roles.map((r) => r.id);

    let filtered = users.filter(
      (user) =>
        Array.isArray(user.role) &&
        user.role.some((r) => superAdminRoleIds.includes(r))
    );

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();

      filtered = filtered.filter(
        (user) =>
          user.email?.toLowerCase().includes(term) ||
          user.mobile_number?.includes(searchTerm)
      );
    }

    if (selectedRole) {
      filtered = filtered.filter(
        (user) =>
          user.role && user.role.includes(parseInt(selectedRole, 10))
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedRole, users, roles]);

  // ---------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------

  const totalPages = Math.ceil(filteredUsers.length / entriesPerPage);

  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;

  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return pages;
    }

    pages.push(1);

    if (currentPage > 3) {
      pages.push("left-dots");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push("right-dots");
    }

    pages.push(totalPages);

    return pages;
  };

  // ---------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------

  const deleteUser = async (userId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete user",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/users/${userId}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "User has been deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete user. Please try again.",
      });
    }
  };

  // ---------------------------------------------------------------------
  // Add / Edit
  // ---------------------------------------------------------------------

  const openAddModal = () => {
    setEditingUser(null);

    setFormData({
      email: "",
      mobile_country_code: "+91",
      mobile_number: "",
      password: "",
      role: "",
      is_active: true,
      mfa_method: "EMAIL",
    });

    setShowAddModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);

    setFormData({
      email: user.email || "",
      mobile_country_code: user.mobile_country_code || "+91",
      mobile_number: user.mobile_number || "",
      password: "",
      role:
        user.role && user.role.length > 0
          ? user.role[0].toString()
          : "",
      is_active: user.is_active,
      mfa_method: user.mfa_method || "EMAIL",
    });

    setShowAddModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------

  const submitUserForm = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing fields",
        text: "Email address is required.",
      });
      return;
    }

    if (!formData.mobile_number.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing fields",
        text: "Mobile number is required.",
      });
      return;
    }

    if (!editingUser && !formData.password) {
      Swal.fire({
        icon: "warning",
        title: "Missing fields",
        text: "Password is required when creating a user.",
      });
      return;
    }

    const payload = {
      email: formData.email.trim(),
      mobile_country_code: formData.mobile_country_code,
      mobile_number: formData.mobile_number.trim(),
      role: formData.role ? [parseInt(formData.role, 10)] : [],
      is_active: formData.is_active,
      mfa_method: formData.mfa_method,
      is_candidate: false,
      is_mentor: false,
      is_staff: false,
      is_superuser: false,
    };

    if (formData.password) {
      payload.password = formData.password;
    }

    if (!editingUser) {
      const superAdmin = getSuperAdminUser();

      if (superAdmin?.id != null) {
        payload.approved_by = superAdmin.id;
      } else {
        console.warn(
          "super_admin_user not found in localStorage - approved_by will be omitted."
        );
      }
    }

    setSaving(true);

    try {
      if (editingUser) {
        await axios.put(
          `${API_BASE}/users/${editingUser.id}/`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "User updated successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await axios.post(`${API_BASE}/users/`, payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "User created successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setShowAddModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Error saving user:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to save user. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="user-table-page-content">
          {/* Header */}
          <div className="user-table-header">
            <div className="user-table-header-left">
              <h2>User Management</h2>

              <span className="user-table-count">
                {filteredUsers.length} users
              </span>
            </div>

            <div className="user-table-header-right">
              <div className="user-table-search-wrapper">
                <FaSearch className="user-table-search-icon" />

                <input
                  type="text"
                  placeholder="Search by email or mobile..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="user-table-search-input"
                />
              </div>

              <div className="user-table-filter-wrapper">
                <FaFilter className="user-table-filter-icon" />

                <select
                  className="user-table-filter-select"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option value="">All Roles</option>

                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="user-table-refresh-btn"
                onClick={fetchUsers}
                title="Refresh"
              >
                <FaSyncAlt />
              </button>

              <button
                className="user-table-add-btn"
                onClick={openAddModal}
              >
                <FaUserPlus />
                Add User
              </button>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="user-table-loader-container">
              <div className="user-table-loader">
                <FaSpinner className="user-table-spinner" />
                <p>Loading users...</p>
              </div>
            </div>
          ) : (
            <div className="user-table-container">
              {filteredUsers.length === 0 ? (
                <div className="user-table-no-data">
                  <div className="user-table-empty-state">
                    <div className="user-table-empty-icon">
                      <FaUserPlus />
                    </div>

                    <p>No users found</p>

                    <span className="user-table-empty-sub">
                      {searchTerm || selectedRole
                        ? "Try adjusting your search or filters"
                        : "Add your first user to get started"}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="user-table-responsive">
                    <table className="user-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Mobile Number</th>
                          <th>Role</th>
                          <th>MFA Method</th>
                          <th>Status</th>
                          <th className="user-table-actions-header">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginatedUsers.map((user) => (
                          <tr key={user.id}>
                            <td>
                              <div className="user-table-name-wrapper">
                                <div className="user-table-avatar">
                                  {user.email
                                    ?.charAt(0)
                                    .toUpperCase()}
                                </div>

                                <div className="user-table-email-wrapper">
                                  <span className="user-table-email">
                                    {user.email}
                                  </span>

                                  {user.is_superuser && (
                                    <span className="user-table-superadmin-badge">
                                      Super Admin
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td>
                              <span className="user-table-mobile">
                                {user.mobile_country_code}{" "}
                                {user.mobile_number}
                              </span>
                            </td>

                            <td>
                              <span className="user-table-role">
                                {getRoleNames(user)}
                              </span>
                            </td>

                            <td>
                              <span className="user-table-mfa">
                                {user.mfa_method || "EMAIL"}
                              </span>
                            </td>

                            <td>
                              <span
                                className={`user-table-status ${
                                  user.is_active
                                    ? "user-table-active"
                                    : "user-table-inactive"
                                }`}
                              >
                                <span className="user-table-status-dot"></span>

                                {user.is_active
                                  ? "ACTIVE"
                                  : "INACTIVE"}
                              </span>
                            </td>

                            <td>
                              <div className="user-table-actions-cell">
                                <button
                                  className="user-table-action-btn user-table-edit-btn"
                                  onClick={() =>
                                    openEditModal(user)
                                  }
                                  title="Edit User"
                                >
                                  <FiEdit2 />
                                </button>

                                <button
                                  className="user-table-action-btn user-table-delete-btn"
                                  onClick={() =>
                                    deleteUser(user.id)
                                  }
                                  title="Delete User"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {filteredUsers.length > 0 && (
                    <div className="user-table-pagination">
                      <div className="user-table-show-entries">
                        <span>Show</span>

                        <select
                          value={entriesPerPage}
                          onChange={handleEntriesChange}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>

                        <span>entries</span>
                      </div>

                      <div className="user-table-pagination-info">
                        Showing <strong>{startIndex + 1}</strong> to{" "}
                        <strong>
                          {Math.min(
                            endIndex,
                            filteredUsers.length
                          )}
                        </strong>{" "}
                        of <strong>{filteredUsers.length}</strong>{" "}
                        users
                      </div>

                      <div className="user-table-pagination-controls">
                        <button
                          className="user-table-page-btn user-table-prev-btn"
                          onClick={() =>
                            goToPage(currentPage - 1)
                          }
                          disabled={currentPage === 1}
                          title="Previous"
                        >
                          <FaChevronLeft />
                        </button>

                        <div className="user-table-page-numbers">
                          {getPageNumbers().map((page, index) =>
                            typeof page === "string" ? (
                              <span
                                key={`${page}-${index}`}
                                className="user-table-page-dots"
                              >
                                ...
                              </span>
                            ) : (
                              <button
                                key={page}
                                className={`user-table-page-btn ${
                                  currentPage === page
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => goToPage(page)}
                              >
                                {page}
                              </button>
                            )
                          )}
                        </div>

                        <button
                          className="user-table-page-btn user-table-next-btn"
                          onClick={() =>
                            goToPage(currentPage + 1)
                          }
                          disabled={currentPage === totalPages}
                          title="Next"
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showAddModal && (
        <div
          className="user-table-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) {
              setShowAddModal(false);
            }
          }}
        >
          <div
            className="user-table-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-table-modal-header">
              <h2>
                {editingUser ? "Edit User" : "Add New User"}
              </h2>

              <button
                className="user-table-modal-close"
                onClick={() => !saving && setShowAddModal(false)}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={submitUserForm}
              className="user-table-modal-form"
            >
              <div className="user-table-form-group">
                <label>Email Address</label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  required
                />
              </div>

              <div className="user-table-form-row">
                <div className="user-table-form-group">
                  <label>Country Code</label>

                  <input
                    type="text"
                    name="mobile_country_code"
                    value={formData.mobile_country_code}
                    onChange={handleInputChange}
                    placeholder="+91"
                    required
                  />
                </div>

                <div className="user-table-form-group">
                  <label>Mobile Number</label>

                  <input
                    type="text"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    placeholder="Enter mobile number"
                    required
                  />
                </div>
              </div>

              <div className="user-table-form-group">
                <label>
                  Password
                  {editingUser
                    ? " (leave blank to keep unchanged)"
                    : ""}
                </label>

                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={
                    editingUser
                      ? "Enter new password (optional)"
                      : "Enter password"
                  }
                  required={!editingUser}
                />
              </div>

              <div className="user-table-form-row">
                <div className="user-table-form-group">
                  <label>Role</label>

                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Role</option>

                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="user-table-form-group">
                  <label>MFA Method</label>

                  <select
                    name="mfa_method"
                    value={formData.mfa_method}
                    onChange={handleInputChange}
                  >
                    {MFA_OPTIONS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="user-table-toggle-group">
                <div className="user-table-toggle-row">
                  <button
                    type="button"
                    className={`user-table-toggle-switch ${
                      formData.is_active
                        ? "user-table-toggle-on"
                        : ""
                    }`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: !prev.is_active,
                      }))
                    }
                    aria-pressed={formData.is_active}
                  >
                    <span className="user-table-toggle-knob"></span>
                  </button>

                  <span className="user-table-toggle-label">
                    Active
                  </span>
                </div>
              </div>

              <div className="user-table-modal-actions">
                <button
                  type="button"
                  className="user-table-cancel-btn"
                  onClick={() =>
                    !saving && setShowAddModal(false)
                  }
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="user-table-submit-btn"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingUser
                    ? "Update User"
                    : "Add User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;